import React, { useState } from 'react';
import { useCompanies } from '@/hooks/useCompanies';
import { useTouristTours } from '@/hooks/useTouristTours';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Building2, Users, Calendar, FileUp, Download, Eye, Plus, MapPin, Clock } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
const CompanyManagement = () => {
  const {
    data: companies,
    isLoading: companiesLoading
  } = useCompanies();
  const {
    data: tours,
    isLoading: toursLoading
  } = useTouristTours();
  const [selectedCompany, setSelectedCompany] = useState<any>(null);
  const [selectedProgram, setSelectedProgram] = useState<any>(null);
  const [isCreateProgramOpen, setIsCreateProgramOpen] = useState(false);

  // برامج سياحية تجريبية لكل شركة
  const [companyPrograms, setCompanyPrograms] = useState<{
    [key: string]: any[];
  }>({});
  const generateProgramsForCompany = (companyId: string) => {
    const programs = [];
    const days = [1, 2, 3, 4, 5, 7, 10];
    const persons = [1, 2, 3, 4, 5, 6, 8, 10];
    for (let day of days) {
      for (let person of persons) {
        programs.push({
          id: `${companyId}-${day}d-${person}p`,
          companyId,
          title: `برنامج ${day} أيام - ${person} أشخاص`,
          days: day,
          persons: person,
          price: day * person * 500 + Math.random() * 1000,
          currency: 'SAR',
          description: `برنامج سياحي لمدة ${day} أيام يشمل ${person} أشخاص`,
          files: [],
          status: 'active',
          createdAt: new Date().toISOString()
        });
      }
    }
    return programs;
  };
  const getCompanyPrograms = (companyId: string) => {
    if (!companyPrograms[companyId]) {
      setCompanyPrograms(prev => ({
        ...prev,
        [companyId]: generateProgramsForCompany(companyId)
      }));
      return generateProgramsForCompany(companyId);
    }
    return companyPrograms[companyId];
  };
  const CompanyCard = ({
    company
  }: {
    company: any;
  }) => <Card className="hover:shadow-lg transition-shadow cursor-pointer" onClick={() => setSelectedCompany(company)}>
      <CardHeader className="pb-3">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg flex items-center justify-center">
            <Building2 className="w-6 h-6 text-white" />
          </div>
          <div className="flex-1">
            <CardTitle className="text-lg text-right">{company.name_ar}</CardTitle>
            <p className="text-sm text-muted-foreground">{company.mobile}</p>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Badge variant="secondary">
              {getCompanyPrograms(company.id).length} برنامج
            </Badge>
            <Badge variant="outline">
              نشط
            </Badge>
          </div>
          {company.address_ar && <p className="text-sm text-muted-foreground flex items-center gap-2">
              <MapPin className="w-4 h-4" />
              {company.address_ar}
            </p>}
        </div>
      </CardContent>
    </Card>;
  const ProgramCard = ({
    program
  }: {
    program: any;
  }) => <Card className="hover:shadow-md transition-shadow">
      <CardHeader className="pb-3">
        <div className="flex justify-between items-start">
          <CardTitle className="text-right text-lg">{program.title}</CardTitle>
          <Badge variant={program.status === 'active' ? 'default' : 'secondary'}>
            {program.status === 'active' ? 'نشط' : 'غير نشط'}
          </Badge>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-blue-500" />
              <span>{program.days} أيام</span>
            </div>
            <div className="flex items-center gap-2">
              <Users className="w-4 h-4 text-green-500" />
              <span>{program.persons} أشخاص</span>
            </div>
          </div>
          
          <div className="flex items-center justify-between">
            <div className="text-lg font-semibold text-blue-600">
              {program.price.toLocaleString()} {program.currency}
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant="outline">
                <FileUp className="w-4 h-4 ml-2" />
                رفع ملف
              </Button>
              <Button size="sm" variant="outline" onClick={() => setSelectedProgram(program)}>
                <Eye className="w-4 h-4 ml-2" />
                عرض
              </Button>
            </div>
          </div>

          {program.files && program.files.length > 0 && <div className="border-t pt-3">
              <p className="text-sm font-medium mb-2">الملفات المرفقة:</p>
              <div className="flex flex-wrap gap-2">
                {program.files.map((file: any, index: number) => <Badge key={index} variant="outline" className="cursor-pointer">
                    <Download className="w-3 h-3 ml-1" />
                    {file.name}
                  </Badge>)}
              </div>
            </div>}
        </div>
      </CardContent>
    </Card>;
  const CreateProgramDialog = () => <Dialog open={isCreateProgramOpen} onOpenChange={setIsCreateProgramOpen}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="text-right">إنشاء برنامج جديد</DialogTitle>
        </DialogHeader>
        <div className="space-y-4" dir="rtl">
          <div>
            <Label>اسم البرنامج</Label>
            <Input placeholder="أدخل اسم البرنامج" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>عدد الأيام</Label>
              <Select>
                <SelectTrigger>
                  <SelectValue placeholder="اختر" />
                </SelectTrigger>
                <SelectContent>
                  {[1, 2, 3, 4, 5, 7, 10, 14].map(days => <SelectItem key={days} value={days.toString()}>
                      {days} أيام
                    </SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>عدد الأشخاص</Label>
              <Select>
                <SelectTrigger>
                  <SelectValue placeholder="اختر" />
                </SelectTrigger>
                <SelectContent>
                  {[1, 2, 3, 4, 5, 6, 8, 10, 15, 20].map(persons => <SelectItem key={persons} value={persons.toString()}>
                      {persons} أشخاص
                    </SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>السعر</Label>
            <div className="flex gap-2">
              <Input placeholder="0" type="number" />
              <Select defaultValue="SAR">
                <SelectTrigger className="w-20">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="SAR">ريال</SelectItem>
                  <SelectItem value="USD">دولار</SelectItem>
                  <SelectItem value="EUR">يورو</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>الوصف</Label>
            <Textarea placeholder="وصف البرنامج السياحي" rows={3} />
          </div>
          <div className="flex gap-2 pt-4">
            <Button className="flex-1">إنشاء البرنامج</Button>
            <Button variant="outline" onClick={() => setIsCreateProgramOpen(false)}>
              إلغاء
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>;
  if (companiesLoading) {
    return <div className="container mx-auto p-6 flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-muted-foreground">جاري تحميل بيانات الشركات...</p>
        </div>
      </div>;
  }
  return <div className="container mx-auto p-6 space-y-6" dir="rtl">
      {/* Header */}
      <div className="flex items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold">إدارة الشركات</h1>
          <p className="text-muted-foreground">إدارة الشركات والبرامج السياحية</p>
        </div>
      </div>

      {!selectedCompany ?
    // عرض قائمة الشركات
    <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {companies?.map(company => <CompanyCard key={company.id} company={company} />)}
          </div>
          
          {(!companies || companies.length === 0) && <div className="text-center py-12">
              <Building2 className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">لا توجد شركات</h3>
              <p className="text-muted-foreground mb-4">ابدأ بإضافة شركة جديدة</p>
              <Button>
                <Plus className="w-4 h-4 ml-2" />
                إضافة شركة
              </Button>
            </div>}
        </div> :
    // عرض تفاصيل الشركة والبرامج
    <div className="space-y-6">
          {/* Company Header */}
          <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-lg p-6">
            <div className="flex justify-between items-start">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg flex items-center justify-center">
                  <Building2 className="w-8 h-8 text-white" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold">{selectedCompany.name_ar}</h2>
                  <p className="text-muted-foreground">{selectedCompany.mobile}</p>
                  {selectedCompany.address_ar && <p className="text-sm text-muted-foreground flex items-center gap-2 mt-1">
                      <MapPin className="w-4 h-4" />
                      {selectedCompany.address_ar}
                    </p>}
                </div>
              </div>
              <Button variant="outline" onClick={() => setSelectedCompany(null)}>
                العودة للقائمة
              </Button>
            </div>
          </div>

          {/* Programs */}
          <Tabs defaultValue="programs" className="w-full">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="programs">البرامج السياحية</TabsTrigger>
              <TabsTrigger value="employees">الموظفين</TabsTrigger>
              <TabsTrigger value="files">الملفات</TabsTrigger>
            </TabsList>
            
            <TabsContent value="programs" className="space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="text-xl font-semibold">
                  البرامج السياحية ({getCompanyPrograms(selectedCompany.id).length})
                </h3>
                <Button onClick={() => setIsCreateProgramOpen(true)}>
                  <Plus className="w-4 h-4 ml-2" />
                  برنامج جديد
                </Button>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {getCompanyPrograms(selectedCompany.id).map(program => <ProgramCard key={program.id} program={program} />)}
              </div>
            </TabsContent>
            
            <TabsContent value="employees" className="space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="text-xl font-semibold">موظفي الشركة</h3>
                <Button>
                  <Plus className="w-4 h-4 ml-2" />
                  إضافة موظف
                </Button>
              </div>
              
              <div className="text-center py-12">
                <Users className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2">لا يوجد موظفين</h3>
                <p className="text-muted-foreground">ابدأ بإضافة موظفين للشركة</p>
              </div>
            </TabsContent>
            
            <TabsContent value="files" className="space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="text-xl font-semibold">ملفات الشركة</h3>
                <Button>
                  <FileUp className="w-4 h-4 ml-2" />
                  رفع ملف
                </Button>
              </div>
              
              <div className="text-center py-12">
                <FileUp className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2">لا توجد ملفات</h3>
                <p className="text-muted-foreground">ابدأ برفع ملفات الشركة</p>
              </div>
            </TabsContent>
          </Tabs>
        </div>}

      <CreateProgramDialog />
    </div>;
};
export default CompanyManagement;